import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { resetAuth, setEmployeePassword } from "../../../slices/authSlice";
import { toast } from "react-toastify";
import Slideshow from "../../../components/Slideshow";
import { slides } from "../../../assets/slideShowData";
import { logo } from "../../../assets/exports";
import { useSearchParams } from "react-router-dom";
import { Eye, EyeClosed } from "lucide-react";
import PasswordSetSuccessModal from "../../../components/PasswordSetSuccessModal";

const SetPasswordPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const auth = useAppSelector((s) => s.auth);
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || "";
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
        password: Yup.string()
            .required("Password required")
            .min(8, "Password must be at least 8 characters"),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref("password")], "Passwords must match")
            .required("Confirm your password")
    }),
    onSubmit: async (values) => {
      try {
        await dispatch(setEmployeePassword({
            token: token as string ?? "",
            password: values.password
        }
        )).unwrap();

        dispatch(resetAuth())
        toast.success("Your password has been set successfully. You can now log in");
        setShowSuccessModal(true);
      } catch (err: any) {
        toast.error(err || "Verfication failed");
      }
    },
  });

  console.log("auth status", auth.status);


  return (
    <div className="min-h-screen lg:flex-row lg:gap-4 lg:justify-between text-left flex items-center justify-center p-1">
      <div className="hidden lg:block lg:w-1/2">
        <Slideshow slides={slides} />
      </div>

       {/* RIGHT - LOGIN FORM */}
      <div className="flex w-full lg:w-1/2 items-center justify-center lg:p-4">
        <div className="w-full bg-white rounded-2xl md:shadow-md p-6 mx-auto sm:max-w-sm md:max-w-md lg:max-w-md">

          {/* RESPONSIVE HEADER WITH IMAGE */}
          <div className="flex flex-col items-center my-5 px-2">
            <img
              src={logo}
              className="lg:w-50 lg:h-50 w-60 h-60 object-contain"
              alt="StorePilot Logo"
            />
            <div className="text-center w-full">
              <p className="text-xl text-gray-500">Set your account password</p>
            </div>
          </div>

          {/* FORMIK FORM GOES HERE (unchanged) */}
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            {/* PASSWORD */}
            <div className="relative">
                <label className="form-label">Password</label>
                <input 
                    name="password" 
                    value={formik.values.password}
                    type={showPassword ? "text" : "password"} 
                    onChange={formik.handleChange} 
                    onBlur={formik.handleBlur}
                    className={`form-input
                        ${formik.touched.password && formik.errors.password ? "border-red-500" : "border-gray-200"}`}
                    placeholder="••••••••"
                />
                <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="show-password"
                >
                    {showPassword ? <EyeClosed /> : <Eye/>}
                </button>
                {formik.touched.password && formik.errors.password && (
                    <p className="text-xs text-red-500 mt-1">{formik.errors.password}</p>
                )}
            </div>

            {/* CONFIRM PASSWORD */}
             <div className="relative">
                <label className="form-label">Confirm Password</label>
                <input 
                    name="confirmPassword" 
                    value={formik.values.confirmPassword}
                    type={showConfirmPassword ? "text" : "password"} 
                    onChange={formik.handleChange} 
                    onBlur={formik.handleBlur}
                    className={`form-input
                        ${formik.touched.confirmPassword && formik.errors.confirmPassword ? "border-red-500" : "border-gray-200"}`}
                    placeholder="••••••••"
                />
                <button
                    type="button"
                    onClick={() => setShowConfirmPassword((s) => !s)}
                    className="show-password"
                >
                    {showConfirmPassword ? <EyeClosed /> : <Eye/>}
                </button>
                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">{formik.errors.confirmPassword}</p>
                )}
            </div>

          {/* SUBMIT */}
          <div>
            <button
              type="submit"
              className="form-btn"
              disabled={auth.status === "loading"}
            >
               {auth.status === "loading" ? (
                  <div className="flex items-center gap-2">
                    <span className="loader"></span>
                    <span>Setting Password...</span>
                  </div>
                ) : (
                  "Set Password"
                )}
            </button>
          </div>
        </form>
        </div>
      </div>

      {!showSuccessModal && (<PasswordSetSuccessModal />)}
    </div>
  );
};

export default SetPasswordPage;
